'use client';

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import DocsSidebar from '@/components/docs/sidebar';
import DocsSearch from '@/components/docs/search';

type DocsNavContextValue = {
  openMenu: () => void;
};

const DocsNavContext = createContext<DocsNavContextValue | null>(null);

export function useDocsMobileNav() {
  return useContext(DocsNavContext);
}

export function DocsMobileNavProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const openMenu = useCallback(() => setOpen(true), []);

  return (
    <DocsNavContext.Provider value={{ openMenu }}>
      {children}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="left"
          className="flex h-full max-h-[100dvh] w-[min(100vw-1rem,20rem)] max-w-[20rem] flex-col gap-0 overflow-hidden border-r bg-white p-0 sm:max-w-[20rem]"
        >
          <SheetHeader className="space-y-1 border-b px-4 py-4 text-left">
            <SheetTitle className="text-base font-semibold">Documentation</SheetTitle>
            <SheetDescription className="text-xs text-gray-500">
              Endpoints, methods, and pages
            </SheetDescription>
          </SheetHeader>
          <div className="shrink-0 border-b px-4 py-3">
            <DocsSearch />
          </div>
          <ScrollArea className="min-h-0 flex-1">
            <div className="pb-8 pt-2">
              <DocsSidebar onNavigate={() => setOpen(false)} />
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
      <div className="pointer-events-none fixed bottom-0 left-0 right-0 z-40 flex justify-end p-4 lg:hidden">
        <Button
          type="button"
          onClick={() => setOpen(true)}
          className="pointer-events-auto h-12 gap-2 rounded-full bg-[#3983f7] px-5 shadow-lg hover:bg-[#2d6bc9]"
          aria-label="Open documentation navigation"
        >
          <Menu className="h-5 w-5" />
          <span className="text-sm font-semibold">Docs menu</span>
        </Button>
      </div>
    </DocsNavContext.Provider>
  );
}
